import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ManagerInfo, PersonPage } from '@up2d8/contracts';
import { useSession } from '../../shared/session';
import { AdminIcon, Btn, DefinitionList, Field, Input, PersonCell, Sect } from '../admin-kit';
import { useMelding } from './people-ui';

/**
 * De sectie "Leidinggevende" bij een medewerker (Stap 17, TEAM-1; sinds BHR-2 in de tab Toegang
 * van M2).
 *
 * Drie dingen bij elkaar, omdat HR ze in dezelfde minuut bekijkt: wie is zijn leidinggevende,
 * wie rapporteert aan hém, en welke groepen beheert hij. Die laatste twee zijn samen het antwoord
 * op de vraag "wat ziet deze man op zijn teamscherm" — en die vraag komt bij support binnen als
 * "waarom ziet hij niemand".
 *
 * **Zoeken en geen keuzelijst.** De eerste versie was een `<select>` met de eerste tweehonderd
 * actieve mensen erin. Dat werkt bij een demobedrijf en bij niets anders: in een fabriek met
 * vierduizend medewerkers staat de gezochte ploegbaas er niet in, en de beheerder ziet niet
 * waarom. Zoeken op naam of nummer schaalt mee en is dezelfde handeling als bij het toevoegen van
 * een groepslid. Het zoekveld staat er pas na "Wijzigen", zoals in M2.
 */
export function PersonManagerSection({ personId }: { personId: string }) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const melding = useMelding();
  const queryClient = useQueryClient();
  const [zoek, setZoek] = useState('');
  const [wijzigen, setWijzigen] = useState(false);

  const canView = has('directory.team.view');
  const canManage = has('directory.person.manage');

  const info = useQuery({
    queryKey: ['directory', 'manager', personId],
    enabled: canView,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/people/{personId}/manager', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ManagerInfo;
    },
  });

  const kandidaten = useQuery({
    enabled: canManage && wijzigen && zoek.trim().length >= 2,
    queryKey: ['directory', 'manager-candidates', zoek],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: zoek.trim(), limit: '10', status: 'active' } } as never,
      });
      return (data as PersonPage | undefined)?.items ?? [];
    },
  });

  const save = useMutation({
    mutationFn: async (managerPersonId: string | null) => {
      const { data, error } = await api.PUT('/api/v1/people/{personId}/manager', {
        params: { path: { personId } },
        body: { managerPersonId },
      });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as ManagerInfo;
    },
    onSuccess: (data) => {
      setZoek('');
      setWijzigen(false);
      queryClient.setQueryData(['directory', 'manager', personId], data);
      melding.ok(t('manager.saved'));
    },
    // De server weigert een kring (ADR-0072); die uitleg hoort HR te lezen en niet "er ging
    // iets mis". De `detail` van het probleem is de zin die daarvoor geschreven is.
    onError: (error: unknown) => {
      const detail = (error as { detail?: string })?.detail;
      melding.fout(detail ? `${t('manager.failed')} ${detail}` : t('manager.failed'));
    },
  });

  if (!canView) return null;
  const detail = info.data;
  const rapporten = detail?.directReports ?? [];
  const groepen = detail?.managedGroups ?? [];

  return (
    <Sect title={t('manager.title')}>
      <div data-testid="person-manager">
        <DefinitionList
          items={[
            [
              t('manager.reportsTo'),
              <span key="manager" data-testid="person-manager-name">
                {detail?.manager ? (
                  <PersonCell name={detail.manager.displayName} />
                ) : (
                  <span className="a-muted">{t('manager.none')}</span>
                )}
              </span>,
            ],
            [
              t('beheer.medewerkers.managesGroups'),
              groepen.length > 0 ? (
                groepen.map((group) => group.name).join(', ')
              ) : (
                <span key="geen" className="a-muted">
                  {t('beheer.medewerkers.none')}
                </span>
              ),
            ],
            /*
              Wat hij zelf ziet. Aantallen en namen samen: dit is geen leesgedrag maar het
              organigram, en dat is geen gegeven dat ADR-0014 beschermt - HR stelt het zelf in.
            */
            [
              t('beheer.medewerkers.directReports'),
              <span key="rapporten" data-testid="person-direct-reports">
                {t('manager.directReports', { count: rapporten.length })}
                {rapporten.length > 0 ? (
                  <span className="a-meta" style={{ display: 'block' }}>
                    {rapporten.map((person) => person.displayName).join(', ')}
                  </span>
                ) : null}
              </span>,
            ],
          ]}
        />
      </div>

      {canManage && !wijzigen ? (
        <div>
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="pencil" />}
            onClick={() => setWijzigen(true)}
            testId="person-manager-edit"
          >
            {t('beheer.medewerkers.change')}
          </Btn>
        </div>
      ) : null}

      {canManage && wijzigen ? (
        <div className="a-stack">
          <Field label={t('manager.search')}>
            {(props) => (
              <Input
                {...props}
                data-testid="person-manager-search"
                value={zoek}
                onChange={(event) => setZoek(event.target.value)}
              />
            )}
          </Field>
          <div className="a-pills">
            {(kandidaten.data ?? [])
              .filter((person) => person.id !== personId)
              .map((person) => (
                <Btn
                  key={person.id}
                  variant="sec"
                  size="sm"
                  testId="person-manager-pick"
                  disabled={save.isPending}
                  onClick={() => save.mutate(person.id)}
                >
                  {person.lastName}, {person.firstName}
                  {person.employeeNumber ? ` · ${person.employeeNumber}` : ''}
                </Btn>
              ))}
          </div>
          <div className="a-actions">
            {detail?.manager ? (
              <Btn
                variant="danger-sec"
                size="sm"
                testId="person-manager-clear"
                disabled={save.isPending}
                onClick={() => save.mutate(null)}
              >
                {t('beheer.medewerkers.managerClear')}
              </Btn>
            ) : null}
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => {
                setWijzigen(false);
                setZoek('');
              }}
            >
              {t('common.cancel')}
            </Btn>
          </div>
        </div>
      ) : null}
      {melding.element}
    </Sect>
  );
}
